import { useState, useEffect } from "react";
import { SelfCertIndividualFormModel } from "../Models/SelfCertIndividualModels";
import { SelfCertIndividualService } from "../services/SelfCertIndividualService";

export interface UseSelfCertIndividualFormOptions {
  autoSaveDraft?: boolean;
  autoSaveInterval?: number;
}

export interface UseSelfCertIndividualFormReturn {
  model: SelfCertIndividualFormModel;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (fieldName: string, value: any) => void;
  updateMultipleFields: (fields: Record<string, any>) => void;
  validate: () => boolean;
}

export const useSelfCertIndividualForm = (
  initialData?: any,
  options: UseSelfCertIndividualFormOptions = {}
): UseSelfCertIndividualFormReturn => {
  const [model, setModel] = useState<SelfCertIndividualFormModel>(() => {
    if (initialData) {
      return SelfCertIndividualService.fromApiResponse(initialData);
    }
    return new SelfCertIndividualFormModel();
  });

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(true);

  const updateField = (fieldName: string, value: any) => {
    setModel((prevModel) => {
      const newModel = new SelfCertIndividualFormModel(prevModel);
      newModel[fieldName] = value;
      return newModel;
    });
  };

  const updateMultipleFields = (fields: Record<string, any>) => {
    setModel((prevModel) => {
      const newModel = new SelfCertIndividualFormModel(prevModel);
      Object.entries(fields).forEach(([key, value]) => {
        newModel[key] = value;
      });
      return newModel;
    });
  };

  const validate = (): boolean => {
    const validationResult = model.validate();
    // Convert string array to Record<string, string> format
    const errorRecord: Record<string, string> = {};
    if (validationResult.errors && Array.isArray(validationResult.errors)) {
      validationResult.errors.forEach((error, index) => {
        errorRecord[`error_${index}`] = error;
      });
    }
    setErrors(errorRecord);
    setIsValid(validationResult.isValid);
    return validationResult.isValid;
  };

  // Auto-validation on model changes
  useEffect(() => {
    validate();
  }, [model]);

  return {
    model,
    isLoading: false,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
